import { Plan } from '../../models/plan.model';
import { Plans } from './plan.constants';

// Existing client limits, shared with the Add Car plan resolver.
// The enforcement cap for Open is unchanged; its presentation uses explicit unlimited semantics.
export const PACKAGE_PLAN_LIMITS: Readonly<Record<Plans, Readonly<Plan>>> = {
  [Plans.Flex]: { name: Plans.Flex, maxAds: 20, maxPhotos: 10, maxTopAds: 1 },
  [Plans.Pro]: { name: Plans.Pro, maxAds: 50, maxPhotos: 16, maxTopAds: 3 },
  [Plans.Unlimited]: { name: Plans.Unlimited, maxAds: 5000, maxPhotos: 20, maxTopAds: 10 },
};

// UI-only reference for Active Ads; does not change subscription limits.
export const ACTIVE_ADS_PROGRESS_REFERENCE = 100;

export type BenefitValue = number | boolean | 'unlimited' | null | undefined;

/** Compare like-for-like benefits without inventing a numeric value for unlimited. */
export function calculateBenefitProgress(value: BenefitValue, comparisons: readonly BenefitValue[]): number {
  if (value === 'unlimited' || value === true) return 100;
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) return 0;
  const maximum = Math.max(0, ...comparisons.filter((item): item is number =>
    typeof item === 'number' && Number.isFinite(item) && item > 0));
  return maximum > 0 ? Math.min(100, Math.max(0, value / maximum * 100)) : 0;
}

type Benefit = 'ads' | 'featured' | 'duration' | 'photos' | 'companyProfile';
const standardFeatures: Readonly<Record<number, Benefit>> = {
  1: 'ads', 2: 'featured', 3: 'duration', 4: 'photos', 5: 'companyProfile',
};
const openFeatures: Readonly<Record<number, Benefit>> = {
  ...standardFeatures, 2: 'duration', 3: 'featured',
};

// Finite ads last one month. Open advertises no ad expiry, with backend expiry
// tied to subscription expiry. These are package benefits, not individual subscription dates.
const packageExtras: Readonly<Record<Plans, { duration: BenefitValue; companyProfile: boolean }>> = {
  [Plans.Flex]: { duration: 1, companyProfile: true },
  [Plans.Pro]: { duration: 1, companyProfile: true },
  [Plans.Unlimited]: { duration: 'unlimited', companyProfile: true },
};

export function packageBenefitProgress(
  plan: Plans, feature: number,
  limits: Readonly<Record<Plans, Readonly<Plan>>> = PACKAGE_PLAN_LIMITS,
  activeAdsReference: number = ACTIVE_ADS_PROGRESS_REFERENCE,
): number {
  const benefit = (plan === Plans.Unlimited ? openFeatures : standardFeatures)[feature];
  if (!benefit) return 0;
  // Package-card presentation override for the finite plans' duration benefit.
  if (benefit === 'duration' && (plan === Plans.Flex || plan === Plans.Pro)) return 50;
  const valueFor = (tier: Plans): BenefitValue => {
    switch (benefit) {
      case 'ads': return tier === Plans.Unlimited ? 'unlimited' : limits[tier].maxAds;
      case 'featured': return limits[tier].maxTopAds;
      case 'photos': return limits[tier].maxPhotos;
      case 'duration': return packageExtras[tier].duration;
      case 'companyProfile': return packageExtras[tier].companyProfile;
    }
  };
  const comparison = benefit === 'ads' ? [activeAdsReference] : Object.values(Plans).map(valueFor);
  return calculateBenefitProgress(valueFor(plan), comparison);
}
